<template>
    <div class="p-5 flex flex-col justify-center items-center w-full" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">

        <!-- Stats Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-5 w-full mb-7">
            <div class="bg-white shadow-md rounded-lg p-5 text-center border border-gray-200">
                <h3 class="text-gray-500 text-sm">{{ $t('admin_dashboard.stats.total_submissions') }}</h3>
                <p class="text-base lg:text-2xl font-semibold text-primary">{{ stats.totalSubmissions }}</p>
            </div>

            <div class="bg-white shadow-md rounded-lg p-5 text-center border border-gray-200">
                <h3 class="text-gray-500 text-sm">{{ $t('admin_dashboard.stats.top_culture') }}</h3>
                <p class="text-base lg:text-2xl font-semibold text-primary truncate">
                    {{ stats.topCulture.replace(/_/g, ' ') }}
                </p>
            </div>

            <div class="bg-white shadow-md rounded-lg p-5 text-center border border-gray-200">
                <h3 class="text-gray-500 text-sm">{{ $t('admin_dashboard.stats.avg_hectares') }}</h3>
                <p class="text-base lg:text-2xl font-semibold text-primary">{{ stats.avgHectares }}</p>
            </div>

            <div class="bg-white shadow-md rounded-lg p-5 text-center border border-gray-200">
                <h3 class="text-gray-500 text-sm">{{ $t('admin_dashboard.stats.top_function') }}</h3>
                <p class="text-base lg:text-2xl font-semibold text-primary">{{ stats.mostPopularFunction }}</p>
            </div>
        </div>

        <!-- Export Button -->
        <div class="flex justify-end items-center w-full mb-2">
            <div @click="downloadExcel"
                class="bg-primary hover:cursor-pointer text-white hover:scale-105 transition duration-200 px-3 py-2 rounded shadow-lg flex space-x-1 text-sm">
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-5">
                        <path stroke-linecap="round" stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                </div>
                <div>{{ $t('admin_dashboard.export') }}</div>
            </div>
        </div>

        <!-- Table -->
        <div class="relative overflow-x-auto shadow-md sm:rounded-lg w-full">
            <Transition>
                <div class="absolute w-full h-full bg-black/30 flex justify-center items-center"
                    v-if="showLoadingModal">
                    <div class=" w-full h-1/2 flex flex-col justify-center p-3 items-center space-y-5">
                        <div class="flex justify-center space-x-2 mt-3">
                            <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-0"></span>
                            <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-150"></span>
                            <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-300"></span>
                        </div>
                    </div>
                </div>
            </Transition>
            <table class="w-full text-sm text-left rtl:text-right text-gray-500">
                <thead class="text-xs text-white bg-primary">
                    <tr>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.last_name') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.first_name') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.phone') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.function') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.company') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.hectares') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.wilaya') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.commune') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.cultures') }}</th>
                        <th class="px-6 py-3">{{ $t('admin_dashboard.table.date') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in subscriptions" :key="index"
                        class="bg-white border-b border-gray-200 hover:bg-gray-50">
                        <td class="px-6 py-4">{{ row.user.last_name }}</td>
                        <td class="px-6 py-4">{{ row.user.first_name }}</td>
                        <td class="px-6 py-4">{{ row.user.phone }}</td>
                        <td class="px-6 py-4">{{ row.function }}</td>
                        <td class="px-6 py-4">{{ row.company }}</td>
                        <td class="px-6 py-4">{{ row.nbr_hectars }}</td>
                        <td class="px-6 py-4">{{ row.wilaya }}</td>
                        <td class="px-6 py-4">{{ row.commune }}</td>
                        <td class="px-6 py-4">{{ row.cultures.join(' , ') }}</td>
                        <td class="px-6 py-4">{{ row.created_at.split('T')[0] }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="mt-4 flex  justify-end items-center w-full">
            <div class="flex flex-col">
                <div class="my-2 text-gray-700">La pagination  </div>
                <div class="flex space-x-2">
                    <button v-if="pagination.prev_page_url" @click="loadData(pagination.prev_page_url)"
                        class="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition">
                        {{ $t('admin_dashboard.pagination.previous') }}
                    </button>

                    <button v-if="pagination.next_page_url" @click="loadData(pagination.next_page_url)"
                        class="bg-primary text-white px-4 py-2 rounded-lg hover:bg-secondary transition">
                        {{ $t('admin_dashboard.pagination.next') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>


<script setup>
import { exportExcelFile, getStats, getSubscriptions } from '@/api/admin';
import { ref, onMounted, Transition } from 'vue';


const subscriptions = ref([]);
const stats = ref({
    totalSubmissions: 0,
    topCulture: 'N/A',
    avgHectares: 0,
    mostPopularFunction: 'Inconnue',
});

const pagination = ref({}); // to store Laravel's pagination data


const showLoadingModal = ref(false);

const loadData = async (url = null) => {
    showLoadingModal.value = true;
    try {
        const subsResponse = await getSubscriptions(url);

        subscriptions.value = subsResponse.data.subscriptions.data.map(sub => ({
            ...sub,
            cultures: Array.isArray(sub.cultures)
                ? sub.cultures
                : typeof sub.cultures === 'string'
                    ? (() => {
                        try {
                            const parsed = JSON.parse(sub.cultures);
                            return Array.isArray(parsed) ? parsed : [parsed];
                        } catch {
                            return sub.cultures ? [sub.cultures] : [];
                        }
                    })()
                    : []
        }));

        pagination.value = subsResponse.data.subscriptions;

        window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {


        console.error('Error loading subscriptions:', error);
    }

    try {
        const statsResponse = await getStats();
        stats.value = statsResponse.data.stats;
    } catch (error) {
        console.error('Error loading stats:', error);
    }
    showLoadingModal.value = false;
};

const downloadExcel = async () => {
    try {
        const response = await exportExcelFile();
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', 'subscriptions.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);

        window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
        console.error('Error downloading Excel:', error);
    }
};

onMounted(() => {
    loadData();
});     
</script>

<style scoped>
@keyframes bounce-wave {

    0%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-8px);
    }
}

.animate-bounce-wave {
    animation: bounce-wave 1.2s infinite ease-in-out;
}

.delay-0 {
    animation-delay: 0s;
}

.delay-150 {
    animation-delay: 0.15s;
}

.delay-300 {
    animation-delay: 0.3s;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Remove arrows for Firefox */
input[type="number"] {
    -moz-appearance: textfield;
}
</style>